/**
 * Static seed data for demo mode.
 * All data is deterministically generated to provide a realistic,
 * production-grade demo experience.
 */

// ─── Demo user ──────────────────────────────────────────────────────────────

export const DEMO_USER = {
  id: 42,
  email: 'demo@lokitrader.com',
  name: 'Demo Trader',
  role: 'OPERATOR' as const,
  is_active: true,
  created_at: '2025-09-01T00:00:00Z',
};

// ─── JWT factory (mirrors tests/utils/mock-jwt.ts) ──────────────────────────

export function createDemoJwt(): string {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const body = btoa(
    JSON.stringify({
      sub: String(DEMO_USER.id),
      email: DEMO_USER.email,
      name: DEMO_USER.name,
      role: DEMO_USER.role,
      // Expire in 24h — proactive refresh fires much later, not during demo session
      exp: Math.floor(Date.now() / 1000) + 86400,
    }),
  );
  return `${header}.${body}.demo-signature`;
}

// ─── Exchange account ────────────────────────────────────────────────────────

export const DEMO_EXCHANGE_ACCOUNT = {
  id: 1,
  user_id: DEMO_USER.id,
  platform: 'BINANCE',
  label: '[DEMO] My Binance Account',
  api_key_masked: 'dmo***7f9a',
  api_secret_masked: 'dmo***sec',
  is_active: true,
  total_budget_cap: '60000',
  account_config: {},
  budget: {
    cap: '60000',
    allocated: '47320',
    headroom: '12680',
  },
  created_at: '2025-09-01T00:00:00Z',
  updated_at: new Date().toISOString(),
};

// ─── Bots ────────────────────────────────────────────────────────────────────

export const DEMO_BOTS = [
  {
    id: 1,
    user_id: DEMO_USER.id,
    name: '[DEMO] Momentum RSI — BTC/USDT',
    status: 'ACTIVE' as const,
    execution_mode: 'PAPER' as const,
    exchange_account_id: 1,
    symbols: ['BTC/USDT'],
    intervals: ['4h'],
    strategy_type: 'RSI_STRATEGY',
    config: {
      strategy_type: 'RSI_STRATEGY',
      rsi_period: 14,
      overbought: 70,
      oversold: 30,
      position_size_pct: 5,
    },
    risk_limits: {
      max_daily_loss: '950',
      max_position_size: '3000',
      max_open_positions: 3,
    },
    created_at: '2025-11-15T09:00:00Z',
    updated_at: new Date().toISOString(),
  },
  {
    id: 2,
    user_id: DEMO_USER.id,
    name: '[DEMO] MACD Cross — ETH/USDT',
    status: 'ACTIVE' as const,
    execution_mode: 'SHADOW' as const,
    exchange_account_id: 1,
    symbols: ['ETH/USDT'],
    intervals: ['1h'],
    strategy_type: 'MACD_STRATEGY',
    config: {
      strategy_type: 'MACD_STRATEGY',
      fast_period: 12,
      slow_period: 26,
      signal_period: 9,
      position_size_pct: 4,
    },
    risk_limits: {
      max_daily_loss: '620',
      max_position_size: '2000',
      max_open_positions: 2,
    },
    created_at: '2025-12-01T14:30:00Z',
    updated_at: new Date().toISOString(),
  },
  {
    id: 3,
    user_id: DEMO_USER.id,
    name: '[DEMO] Breakout Grid — SOL/USDT',
    status: 'ACTIVE' as const,
    execution_mode: 'PAPER' as const,
    exchange_account_id: 1,
    symbols: ['SOL/USDT'],
    intervals: ['15m'],
    strategy_type: 'GRID_STRATEGY',
    config: {
      strategy_type: 'GRID_STRATEGY',
      grid_levels: 10,
      grid_spacing_pct: 0.5,
      position_size_pct: 3,
    },
    risk_limits: {
      max_daily_loss: '350',
      max_position_size: '1200',
      max_open_positions: 5,
    },
    created_at: '2026-01-10T08:00:00Z',
    updated_at: new Date().toISOString(),
  },
];

// ─── Bot runtime state (module-level mutable — resets on worker restart) ─────

export type BotRuntimeState = 'running' | 'stopped' | 'halted' | 'crashed' | 'starting';

export interface DemoBotState {
  runtime_state: BotRuntimeState;
  allowed_actions: string[];
  cycle_count: number;
  last_heartbeat_at: string | null;
  last_error: string | null;
  restart_count: number;
  halt_reason?: string | null;
  uptime_seconds: number;
}

export const INITIAL_BOT_STATES: Record<number, DemoBotState> = {
  1: {
    runtime_state: 'running',
    allowed_actions: ['stop', 'halt', 'restart'],
    cycle_count: 847,
    last_heartbeat_at: new Date().toISOString(),
    last_error: null,
    restart_count: 0,
    halt_reason: null,
    uptime_seconds: 259200, // 3 days
  },
  2: {
    runtime_state: 'stopped',
    allowed_actions: ['start'],
    cycle_count: 0,
    last_heartbeat_at: null,
    last_error: null,
    restart_count: 0,
    halt_reason: null,
    uptime_seconds: 0,
  },
  3: {
    runtime_state: 'halted',
    allowed_actions: ['resume'],
    cycle_count: 412,
    last_heartbeat_at: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString(),
    last_error: null,
    restart_count: 0,
    halt_reason: 'Daily loss limit exceeded: -2.3% (-$350.40)',
    uptime_seconds: 0,
  },
};

// ─── Equity curve (90 days, realistic growth + drawdown) ─────────────────────

function generateEquityCurve(days: number): Array<{ timestamp: string; total_equity: string }> {
  // Predetermined daily returns (%) — realistic path with drawdown + recovery
  const dailyReturns = [
    // Week 1-2: mild uptrend
    0.0032, 0.0048, -0.0011, 0.0041, 0.0027, 0.0059, -0.0019, 0.0035, 0.0051, 0.0018, 0.0044,
    0.0029, -0.0022, 0.0038, 0.0046,
    // Week 3-5: drawdown period (circuit breaker fired on bot 3)
    -0.0082, -0.0121, -0.0064, -0.0093, -0.0071, -0.0108, -0.0079, -0.0042, -0.0067, -0.0058,
    -0.0035, -0.0049, 0.0012, -0.0028, 0.0021,
    // Week 6-8: recovery
    0.0078, 0.0112, 0.0069, 0.0094, 0.0063, 0.0102, 0.0081, 0.0054, 0.0071, 0.0087, 0.0065, 0.0079,
    0.0103, 0.0072, 0.0061,
    // Week 9-11: steady growth
    0.0034, 0.0042, 0.0021, 0.0051, 0.0038, 0.0016, 0.0043, 0.0035, 0.0054, 0.0024, 0.0041, 0.0033,
    0.0019, 0.0047, 0.0031, 0.0044, 0.0025, 0.0038, 0.0053, 0.0017, 0.0042, 0.0031, 0.0024, 0.0052,
    0.0029,
    // Week 12: minor consolidation
    0.0021, 0.0014, -0.0009, 0.0018, 0.0011, 0.0009, -0.0007, 0.0019, 0.0013, 0.0001, 0.0018,
    0.0012, 0.0003, 0.0014, 0.0021,
    // Week 13: strong finish
    0.0044, 0.0058, 0.0031, 0.0062, 0.0047, 0.0039, 0.0061, 0.0042, 0.0053, 0.0035,
  ];

  let equity = 38000;
  const now = new Date();
  const result: Array<{ timestamp: string; total_equity: string }> = [];

  for (let i = days; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    date.setUTCHours(0, 0, 0, 0);

    const idx = days - i;
    if (idx > 0 && idx <= dailyReturns.length) {
      equity = equity * (1 + (dailyReturns[idx - 1] ?? 0));
    }

    result.push({ timestamp: date.toISOString(), total_equity: equity.toFixed(2) });
  }

  return result;
}

export const DEMO_EQUITY_CURVE = generateEquityCurve(89);

// ─── Bot equity (per-bot, shorter curves) ────────────────────────────────────

function generateBotEquity(
  days: number,
  startEquity: number,
  returnsTemplate: number[],
): Array<{
  id: number;
  snapshot_time: string;
  cash: string;
  total_equity: string;
  positions_json: unknown[];
}> {
  let equity = startEquity;
  const now = new Date();
  return Array.from({ length: days }, (_, i) => {
    const date = new Date(now);
    date.setDate(date.getDate() - (days - 1 - i));
    date.setUTCHours(0, 0, 0, 0);
    const ret = returnsTemplate[i % returnsTemplate.length] ?? 0;
    equity = equity * (1 + ret);
    return {
      id: i + 1,
      snapshot_time: date.toISOString(),
      cash: (equity * 0.6).toFixed(2),
      total_equity: equity.toFixed(2),
      positions_json: [],
    };
  });
}

export const DEMO_BOT1_EQUITY = generateBotEquity(
  30,
  23150,
  [0.004, -0.002, 0.006, 0.003, -0.001, 0.007, 0.002, -0.003, 0.005, 0.004],
);

export const DEMO_BOT2_EQUITY = generateBotEquity(
  20,
  15420,
  [0.002, -0.001, 0.003, 0.001, -0.002, 0.004, 0.001, -0.001, 0.003, 0.002],
);

export const DEMO_BOT3_EQUITY = generateBotEquity(
  25,
  8750,
  [0.003, 0.005, -0.008, -0.011, -0.007, -0.009, 0.001, 0.002, -0.003, 0.001],
);

// ─── Daily PnL (per-bot) ─────────────────────────────────────────────────────

function generateDailyPnl(
  days: number,
  dailyPnlValues: number[],
  startEquity: number,
): Array<{
  id: number;
  trading_day_utc: string;
  daily_pnl: string;
  start_equity: string;
  last_updated_at: string;
}> {
  const now = new Date();
  return Array.from({ length: days }, (_, i) => {
    const date = new Date(now);
    date.setDate(date.getDate() - (days - 1 - i));
    const pnl = dailyPnlValues[i % dailyPnlValues.length] ?? 0;
    const dayEquity = startEquity + pnl * i * 0.3;
    return {
      id: i + 1,
      trading_day_utc: date.toISOString().split('T')[0] ?? '',
      daily_pnl: pnl.toFixed(2),
      start_equity: dayEquity.toFixed(2),
      last_updated_at: date.toISOString(),
    };
  });
}

export const DEMO_BOT1_PNL = generateDailyPnl(
  30,
  [85.2, -42.1, 112.7, 67.3, -28.9, 143.5, 52.1, -35.6, 98.4, 76.8],
  23150,
);

export const DEMO_BOT2_PNL = generateDailyPnl(
  20,
  [34.1, -18.7, 52.3, 28.9, -12.4, 61.7, 22.3, -14.8, 41.2, 31.5],
  15420,
);

export const DEMO_BOT3_PNL = generateDailyPnl(
  25,
  [45.3, 67.8, -89.2, -112.4, -78.1, -95.6, 12.3, 18.7, -42.1, 8.9],
  8750,
);

// ─── Positions ───────────────────────────────────────────────────────────────

export const DEMO_POSITIONS = [
  {
    id: 1,
    bot_id: 1,
    symbol: 'BTC/USDT',
    side: 'LONG',
    entry_price: '67240.50',
    current_price: '68450.00',
    quantity: '0.035',
    unrealized_pnl: '42.33',
    pnl_pct: '1.80',
    entry_time: new Date(Date.now() - 6 * 3600 * 1000).toISOString(),
    status: 'OPEN',
  },
  {
    id: 2,
    bot_id: 1,
    symbol: 'BTC/USDT',
    side: 'LONG',
    entry_price: '66890.00',
    current_price: '68450.00',
    quantity: '0.022',
    unrealized_pnl: '34.32',
    pnl_pct: '2.33',
    entry_time: new Date(Date.now() - 14 * 3600 * 1000).toISOString(),
    status: 'OPEN',
  },
  {
    id: 3,
    bot_id: 3,
    symbol: 'SOL/USDT',
    side: 'LONG',
    entry_price: '198.50',
    current_price: '185.20',
    quantity: '12.5',
    unrealized_pnl: '-166.25',
    pnl_pct: '-6.70',
    entry_time: new Date(Date.now() - 50 * 3600 * 1000).toISOString(),
    status: 'OPEN',
  },
  {
    id: 4,
    bot_id: 3,
    symbol: 'SOL/USDT',
    side: 'LONG',
    entry_price: '201.30',
    current_price: '185.20',
    quantity: '8.0',
    unrealized_pnl: '-128.80',
    pnl_pct: '-8.00',
    entry_time: new Date(Date.now() - 52 * 3600 * 1000).toISOString(),
    status: 'OPEN',
  },
];

// ─── Orders ──────────────────────────────────────────────────────────────────

function makeOrder(
  id: number,
  botId: number,
  symbol: string,
  side: 'BUY' | 'SELL',
  price: string,
  quantity: string,
  status: 'FILLED' | 'CANCELLED' | 'PENDING',
  daysAgo: number,
): object {
  const dt = new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString();
  return {
    id,
    bot_id: botId,
    symbol,
    side,
    price,
    quantity,
    filled_quantity: status === 'FILLED' ? quantity : '0',
    remaining_quantity: status === 'FILLED' ? '0' : quantity,
    status,
    signal: side === 'BUY' ? 'RSI_OVERSOLD' : 'RSI_OVERBOUGHT',
    strategy_name: botId === 1 ? 'RSI_STRATEGY' : botId === 2 ? 'MACD_STRATEGY' : 'GRID_STRATEGY',
    created_at: dt,
    submitted_at: dt,
    filled_at: status === 'FILLED' ? dt : null,
    cancelled_at: status === 'CANCELLED' ? dt : null,
    fee: status === 'FILLED' ? (Number(price) * Number(quantity) * 0.001).toFixed(4) : null,
    avg_slippage_bps: status === 'FILLED' ? Math.floor(Math.random() * 8) + 1 : null,
    reject_reason: null,
    correlation_id: `demo-${id}-${Date.now()}`,
    client_order_id: `loki-demo-${id}`,
  };
}

export const DEMO_ORDERS = [
  // Bot 1 - BTC/USDT RSI
  makeOrder(1, 1, 'BTC/USDT', 'BUY', '66890.00', '0.022', 'FILLED', 14),
  makeOrder(2, 1, 'BTC/USDT', 'BUY', '67240.50', '0.035', 'FILLED', 6),
  makeOrder(3, 1, 'BTC/USDT', 'SELL', '69120.00', '0.018', 'FILLED', 10),
  makeOrder(4, 1, 'BTC/USDT', 'BUY', '65780.00', '0.041', 'FILLED', 20),
  makeOrder(5, 1, 'BTC/USDT', 'SELL', '68340.00', '0.041', 'FILLED', 17),
  makeOrder(6, 1, 'BTC/USDT', 'BUY', '63450.00', '0.052', 'FILLED', 28),
  makeOrder(7, 1, 'BTC/USDT', 'SELL', '66700.00', '0.052', 'FILLED', 25),
  makeOrder(8, 1, 'BTC/USDT', 'BUY', '64120.00', '0.038', 'FILLED', 32),
  makeOrder(9, 1, 'BTC/USDT', 'SELL', '67800.00', '0.038', 'FILLED', 29),
  makeOrder(10, 1, 'BTC/USDT', 'BUY', '65210.00', '0.044', 'CANCELLED', 35),
  makeOrder(11, 1, 'BTC/USDT', 'BUY', '62890.00', '0.059', 'FILLED', 42),
  makeOrder(12, 1, 'BTC/USDT', 'SELL', '65400.00', '0.059', 'FILLED', 40),
  makeOrder(13, 1, 'BTC/USDT', 'BUY', '63780.00', '0.047', 'FILLED', 48),
  makeOrder(14, 1, 'BTC/USDT', 'SELL', '67100.00', '0.047', 'FILLED', 45),
  makeOrder(15, 1, 'BTC/USDT', 'BUY', '61200.00', '0.063', 'FILLED', 55),

  // Bot 2 - ETH/USDT MACD (shadow mode)
  makeOrder(16, 2, 'ETH/USDT', 'BUY', '3420.50', '1.850', 'FILLED', 18),
  makeOrder(17, 2, 'ETH/USDT', 'SELL', '3680.00', '1.850', 'FILLED', 14),
  makeOrder(18, 2, 'ETH/USDT', 'BUY', '3350.00', '2.100', 'FILLED', 25),
  makeOrder(19, 2, 'ETH/USDT', 'SELL', '3590.00', '2.100', 'FILLED', 21),
  makeOrder(20, 2, 'ETH/USDT', 'BUY', '3280.00', '2.450', 'FILLED', 33),
  makeOrder(21, 2, 'ETH/USDT', 'SELL', '3510.00', '2.450', 'FILLED', 29),
  makeOrder(22, 2, 'ETH/USDT', 'BUY', '3410.00', '1.920', 'CANCELLED', 40),
  makeOrder(23, 2, 'ETH/USDT', 'BUY', '3190.00', '2.800', 'FILLED', 45),
  makeOrder(24, 2, 'ETH/USDT', 'SELL', '3380.00', '2.800', 'FILLED', 42),
  makeOrder(25, 2, 'ETH/USDT', 'BUY', '3090.00', '3.100', 'FILLED', 52),

  // Bot 3 - SOL/USDT Grid (halted, has positions stuck)
  makeOrder(26, 3, 'SOL/USDT', 'BUY', '198.50', '12.500', 'FILLED', 50),
  makeOrder(27, 3, 'SOL/USDT', 'BUY', '201.30', '8.000', 'FILLED', 52),
  makeOrder(28, 3, 'SOL/USDT', 'BUY', '195.80', '15.200', 'FILLED', 58),
  makeOrder(29, 3, 'SOL/USDT', 'SELL', '210.50', '15.200', 'FILLED', 55),
  makeOrder(30, 3, 'SOL/USDT', 'BUY', '188.20', '18.400', 'FILLED', 65),
  makeOrder(31, 3, 'SOL/USDT', 'SELL', '205.70', '18.400', 'FILLED', 62),
  makeOrder(32, 3, 'SOL/USDT', 'BUY', '172.40', '22.100', 'FILLED', 72),
  makeOrder(33, 3, 'SOL/USDT', 'SELL', '189.80', '22.100', 'FILLED', 69),
];

// ─── Portfolio summary ───────────────────────────────────────────────────────

export const DEMO_PORTFOLIO_SUMMARY = {
  total_cash: '23750.00',
  total_positions: 4,
  total_exposure: '23570.00',
  bots: [
    {
      bot_id: 1,
      bot_name: '[DEMO] Momentum RSI — BTC/USDT',
      cash: '15000.00',
      positions_count: 2,
      total_exposure: '8150.00',
    },
    {
      bot_id: 2,
      bot_name: '[DEMO] MACD Cross — ETH/USDT',
      cash: '15420.00',
      positions_count: 0,
      total_exposure: '0.00',
    },
    {
      bot_id: 3,
      bot_name: '[DEMO] Breakout Grid — SOL/USDT',
      cash: '3750.00',
      positions_count: 2,
      total_exposure: '4254.50', // 2 stuck SOL positions × market price
    },
  ],
};

// ─── Bot portfolio summaries ─────────────────────────────────────────────────

export const DEMO_BOT_PORTFOLIOS: Record<number, object> = {
  1: {
    allocated_capital: '23150.00',
    bot_id: 1,
    bot_name: '[DEMO] Momentum RSI — BTC/USDT',
    capital_utilization_pct: 35.2,
    cash: '15000.00',
    daily_pnl: '890.40',
    daily_pnl_pct: '3.85',
    total_equity: '23150.00',
    total_exposure: '8150.00',
    open_positions: 2,
    win_rate: '64.7',
    sharpe_ratio: '1.58',
    max_drawdown: '-6.2',
    total_trades: 15,
  },
  2: {
    allocated_capital: '15420.00',
    bot_id: 2,
    bot_name: '[DEMO] MACD Cross — ETH/USDT',
    capital_utilization_pct: 0,
    cash: '15420.00',
    daily_pnl: '0.00',
    daily_pnl_pct: '0.00',
    total_equity: '15420.00',
    total_exposure: '0.00',
    open_positions: 0,
    win_rate: '60.0',
    sharpe_ratio: '1.24',
    max_drawdown: '-5.1',
    total_trades: 10,
  },
  3: {
    allocated_capital: '8750.00',
    bot_id: 3,
    bot_name: '[DEMO] Breakout Grid — SOL/USDT',
    capital_utilization_pct: 57.2,
    cash: '3750.00',
    daily_pnl: '-312.40',
    daily_pnl_pct: '-3.57',
    total_equity: '8750.00',
    total_exposure: '5000.00',
    open_positions: 2,
    win_rate: '55.6',
    sharpe_ratio: '0.87',
    max_drawdown: '-8.3',
    total_trades: 8,
  },
};

// ─── Bot summaries ───────────────────────────────────────────────────────────

export const DEMO_BOT_SUMMARIES: Record<number, object> = {
  1: {
    bot_id: 1,
    total_trades: 15,
    winning_trades: 10,
    losing_trades: 4,
    cancelled_orders: 1,
    win_rate: '64.71',
    total_pnl: '1842.70',
    avg_pnl_per_trade: '122.85',
    best_trade_pnl: '520.00',
    worst_trade_pnl: '-142.30',
    avg_holding_time_minutes: '216.40',
    sharpe_ratio: '1.58',
    max_drawdown_pct: '6.20',
  },
  2: {
    bot_id: 2,
    total_trades: 10,
    winning_trades: 6,
    losing_trades: 3,
    cancelled_orders: 1,
    win_rate: '60.00',
    total_pnl: '876.40',
    avg_pnl_per_trade: '87.64',
    best_trade_pnl: '348.00',
    worst_trade_pnl: '-98.50',
    avg_holding_time_minutes: '310.50',
    sharpe_ratio: '1.24',
    max_drawdown_pct: '5.10',
  },
  3: {
    bot_id: 3,
    total_trades: 8,
    winning_trades: 5,
    losing_trades: 2,
    cancelled_orders: 1,
    win_rate: '55.56',
    total_pnl: '-295.30',
    avg_pnl_per_trade: '-36.91',
    best_trade_pnl: '312.80',
    worst_trade_pnl: '-350.40',
    avg_holding_time_minutes: '180.20',
    sharpe_ratio: '0.87',
    max_drawdown_pct: '8.30',
  },
};

// ─── Risk policies ───────────────────────────────────────────────────────────

export const DEMO_RISK_POLICIES: Record<number, object> = {
  1: {
    id: 1,
    bot_id: 1,
    max_daily_loss: '950.00',
    max_position_size: '3000.00',
    max_open_positions: 3,
    max_drawdown_pct: '10.0',
    circuit_breaker_level: 'L0',
    created_at: '2025-11-15T09:00:00Z',
    updated_at: new Date().toISOString(),
  },
  2: {
    id: 2,
    bot_id: 2,
    max_daily_loss: '620.00',
    max_position_size: '2000.00',
    max_open_positions: 2,
    max_drawdown_pct: '8.0',
    circuit_breaker_level: 'L0',
    created_at: '2025-12-01T14:30:00Z',
    updated_at: new Date().toISOString(),
  },
  3: {
    id: 3,
    bot_id: 3,
    max_daily_loss: '350.00',
    max_position_size: '1200.00',
    max_open_positions: 5,
    max_drawdown_pct: '8.0',
    circuit_breaker_level: 'L3', // Tripped — caused the halt
    created_at: '2026-01-10T08:00:00Z',
    updated_at: new Date().toISOString(),
  },
};

// ─── Backtests ───────────────────────────────────────────────────────────────

const backtestEquityPoints = Array.from({ length: 120 }, (_, i) => {
  const startDate = new Date('2024-09-01T00:00:00Z');
  startDate.setDate(startDate.getDate() + i);
  const baseEquity = 10000;
  // Simulate uptrend with noise
  const equity = baseEquity * Math.pow(1.0015, i) * (0.98 + 0.04 * Math.sin(i / 7));
  return {
    timestamp: startDate.toISOString(),
    equity: equity.toFixed(2),
  };
});

export const DEMO_BACKTESTS = [
  {
    id: 1,
    user_id: DEMO_USER.id,
    bot_config: {
      strategy_type: 'RSI_STRATEGY',
      rsi_period: 14,
      overbought: 70,
      oversold: 30,
    },
    exchange_account_id: 1,
    symbol: 'BTC/USDT',
    interval: '4h',
    start_date: '2024-09-01',
    end_date: '2024-12-31',
    initial_capital: '10000.00',
    status: 'COMPLETED',
    final_equity: '14280.50',
    total_return_pct: '42.81',
    total_trades: 89,
    winning_trades: 57,
    losing_trades: 32,
    win_rate: '64.04',
    sharpe_ratio: '1.74',
    max_drawdown_pct: '7.23',
    avg_trade_pnl: '48.09',
    created_at: '2026-01-20T10:00:00Z',
    completed_at: '2026-01-20T10:02:14Z',
    equity_curve: backtestEquityPoints,
  },
  {
    id: 2,
    user_id: DEMO_USER.id,
    bot_config: {
      strategy_type: 'MACD_STRATEGY',
      fast_period: 12,
      slow_period: 26,
      signal_period: 9,
    },
    exchange_account_id: 1,
    symbol: 'ETH/USDT',
    interval: '1h',
    start_date: '2024-10-01',
    end_date: '2024-12-31',
    initial_capital: '10000.00',
    status: 'COMPLETED',
    final_equity: '11850.20',
    total_return_pct: '18.50',
    total_trades: 124,
    winning_trades: 74,
    losing_trades: 50,
    win_rate: '59.68',
    sharpe_ratio: '1.21',
    max_drawdown_pct: '9.14',
    avg_trade_pnl: '14.92',
    created_at: '2026-02-01T14:30:00Z',
    completed_at: '2026-02-01T14:31:48Z',
    equity_curve: [],
  },
];
